/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {useProductList, EXCLUDED_FILTERS_LIST} from '../util/product-list-context'
import {useSortUrls} from '@salesforce/retail-react-app/app/hooks'

import {
    Stack,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import CheckboxRefinements from './checkbox-refinements'
import LinkRefinements from './link-refinements'
import RadioSortingOptions from './radio-sorting-options'

import {PlusIcon, MinusIcon} from '../../../components/custom-icons'
import {extendRefinementExclusions} from '../../../utils/plp-utils'
/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: LinkRefinements,
    c_subCategoryID: LinkRefinements,
    c_orliweb_color_code_rgt: ColorRefinements,
    c_size: SizeRefinements
}

const Refinements = ({
    isExcludedFilters = true,
    filters = [],
    sortingOptions = [],
    selectedFilters,
    isLoading
}) => {
    const params = useParams()
    extendRefinementExclusions(params, EXCLUDED_FILTERS_LIST)

    const {formatMessage} = useIntl()
    const {onCloseRefinements, selectedSortingOptionLabel} = useProductList()

    // Get urls to be used for sorting
    const sortUrls = useSortUrls({options: sortingOptions})

    // Exclude filters in the exclude list.
    if (isExcludedFilters) {
        filters = filters.filter(({attributeId}) => !EXCLUDED_FILTERS_LIST.includes(attributeId))
    }

    // Getting the indixes of sorting + filters to open all accordion items by default
    let filtersIndexes = filters.map((_, idx) => idx)
    if (sortUrls) filtersIndexes.push(filters.length)

    return (
        <Stack
            as="section"
            aria-label={formatMessage({
                defaultMessage: 'Product filters',
                id: 'refinements.region.aria_label'
            })}
        >
            {/* Wait to have filters before rendering the Accordion to allow the default indexes to be accurate */}
            {/* Filter ADA: ul lists */}
            {filtersIndexes && (
                <Accordion
                    as="ul"
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    allowMultiple={true}
                    defaultIndex={filtersIndexes}
                    reduceMotion={true}
                >
                    {/* Sort By */}
                    {sortUrls && (
                        <AccordionItem
                            key="sort-by"
                            as="li"
                            listStyleType="none"
                            border="none"
                            padding={0}
                        >
                            {({isExpanded}) => (
                                <>
                                    {/* Using role="heading" instead of a semantic <h2> tag to avoid SEO impact */}
                                    <Text as="div" role="heading" aria-level="2">
                                        <AccordionButton
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={10}
                                            py={20}
                                        >
                                            <Text as="span" align="left" textStyle="bodyLarge2">
                                                {formatMessage({
                                                    defaultMessage: 'Sort by',
                                                    id: 'refinements.sort_by.title'
                                                })}
                                            </Text>
                                            {isExpanded ? (
                                                <MinusIcon boxSize="12px" />
                                            ) : (
                                                <PlusIcon boxSize="12px" />
                                            )}
                                        </AccordionButton>
                                    </Text>

                                    <AccordionPanel paddingTop={0} paddingBottom={10}>
                                        <RadioSortingOptions
                                            sortUrls={sortUrls}
                                            sortingOptions={sortingOptions}
                                            selectedSortingOptionLabel={selectedSortingOptionLabel}
                                        />
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    )}

                    {filters?.map((filter, idx) => {
                        // Render the appropriate component for the refinement type, fallback to checkboxes
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                        // Catch any non-array values and wrap them in an array
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        }

                        if (filter.values) {
                            return (
                                <AccordionItem
                                    key={`filter-accordion-item-${filter.attributeId}`}
                                    as="li"
                                    listStyleType="none"
                                    border="none"
                                    padding={0}
                                >
                                    {({isExpanded}) => (
                                        <>
                                            {/* Using role="heading" instead of a semantic <h2> tag to avoid SEO impact */}
                                            <Text as="div" role="heading" aria-level="2">
                                                <AccordionButton
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    gap={10}
                                                    py={20}
                                                >
                                                    <Text
                                                        as="span"
                                                        align="left"
                                                        textStyle="bodyLarge2"
                                                    >
                                                        {filter.label}
                                                    </Text>
                                                    {isExpanded ? (
                                                        <MinusIcon boxSize="12px" />
                                                    ) : (
                                                        <PlusIcon boxSize="12px" />
                                                    )}
                                                </AccordionButton>
                                            </Text>

                                            <AccordionPanel paddingTop={0} paddingBottom={10}>
                                                <Values
                                                    selectedFilters={selectedFiltersArray}
                                                    filter={filter}
                                                    {...(Values === LinkRefinements && {
                                                        onSelect: onCloseRefinements
                                                    })}
                                                />
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    filters: PropTypes.array,
    sortingOptions: PropTypes.array,
    isExcludedFilters: PropTypes.bool,
    selectedFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isLoading: PropTypes.bool
}

export default Refinements
